import { Check } from "lucide-react";
import { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import {
  downloadCsv,
  useEnterpriseProgress,
  useFilterOptions,
} from "../api/hooks";
import { PROGRESS_STEPS, type EnterpriseFilterState } from "../api/types";
import { formatDate } from "../lib/format";
import { EnterpriseFilters } from "./EnterpriseFilters";
import { PaginationBar, QueryState } from "./QueryState";

const PAGE_SIZE = 50;

export function ProgressView() {
  const { t, i18n } = useTranslation();
  const [filters, setFilters] = useState<EnterpriseFilterState>({});
  const [page, setPage] = useState(1);

  const { data: options } = useFilterOptions();
  const { data, isLoading, isError, isFetching } = useEnterpriseProgress(
    filters,
    page,
  );

  const applyFilters = (next: EnterpriseFilterState) => {
    setFilters(next);
    setPage(1);
  };

  const handleExport = async () => {
    try {
      await downloadCsv(
        "/api/v1/reports/enterprises/progress/csv/",
        filters,
        "progression.csv",
      );
    } catch {
      toast.error(t("reports.csv.failed"));
    }
  };

  return (
    <div className="space-y-4">
      <EnterpriseFilters
        value={filters}
        onChange={applyFilters}
        options={options}
        onExport={handleExport}
        exportLabel={t("reports.csv.exportProgress")}
      />

      <QueryState
        isLoading={isLoading}
        isError={isError}
        isEmpty={!isFetching && data?.count === 0}
      >
        {data ? (
          <>
            {/* Mobile: one card per enterprise listing each step's reached date;
                the desktop matrix (name + 7 step columns) is too wide for a phone. */}
            <ul className="space-y-2 md:hidden">
              {data.results.map((row) => (
                <li key={row.id} className="rounded-lg border bg-card p-4">
                  <p className="font-semibold text-foreground">{row.display_name}</p>
                  <dl className="mt-2 grid grid-cols-[1fr_auto] gap-x-3 gap-y-1 text-sm">
                    {PROGRESS_STEPS.map((step, index) => {
                      const reached = row[step];
                      return (
                        <Fragment key={step}>
                          <dt className="text-muted-foreground">
                            {index}. {t(`reports.steps.${step}`)}
                          </dt>
                          <dd className="text-right">
                            {reached ? (
                              <span className="inline-flex items-center gap-1 whitespace-nowrap text-xs font-medium">
                                <Check className="size-3.5 shrink-0 text-success" aria-hidden />
                                {formatDate(reached, i18n.language)}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </dd>
                        </Fragment>
                      );
                    })}
                  </dl>
                </li>
              ))}
            </ul>

            <div className="hidden overflow-x-auto rounded-lg border md:block">
              <table className="w-full border-collapse text-sm">
                <thead className="bg-muted">
                  <tr>
                    <th className="sticky left-0 z-10 bg-muted px-3 py-2 text-left font-semibold">
                      {t("reports.columns.name")}
                    </th>
                    {PROGRESS_STEPS.map((step, index) => (
                      <th
                        key={step}
                        className="min-w-24 px-3 py-2 text-center align-bottom font-semibold"
                      >
                        <span className="mx-auto grid size-5 place-items-center rounded-full border border-border bg-background text-xs font-bold tabular-nums">
                          {index}
                        </span>
                        <span className="mt-1 block text-[11px] font-medium text-muted-foreground">
                          {t(`reports.steps.${step}`)}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.results.map((row) => (
                    <tr key={row.id} className="border-b hover:bg-accent/40">
                      <td className="sticky left-0 bg-background px-3 py-2 font-medium">
                        {row.display_name}
                      </td>
                      {PROGRESS_STEPS.map((step) => {
                        const reached = row[step];
                        return (
                          <td key={step} className="px-3 py-2 text-center align-middle">
                            {reached ? (
                              <span className="inline-flex items-center gap-1 whitespace-nowrap text-xs font-medium">
                                <Check
                                  className="size-3.5 shrink-0 text-success"
                                  aria-hidden
                                />
                                {formatDate(reached, i18n.language)}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <PaginationBar
              page={page}
              count={data.count}
              pageSize={PAGE_SIZE}
              onPage={setPage}
            />
          </>
        ) : null}
      </QueryState>
    </div>
  );
}
